import { createContext } from 'react';
import {
	IItemForGetShortInfo,
	TFileId,
	TLanguageIDs,
	IFileData,
	IServerError,
	IGameApplicationIdValidationError,
	IEditGame,
	IGameTranslation,
} from 'types';

export type TEditGameVisibleForm = 'mainForm' | 'translations';
export type TSourceUrlLocally = Nullable<string>;
export type TEditGameSelectedItem = Nullable<string>;
export type TTypeOfEditGameModalWindow =
	| null
	| 'toConfirmFieldsClearing'
	| 'toConfirmSubmit'
	| 'success'
	| 'error'
	| 'imgSizeExceeded'
	| 'fileSizeExceeded'
	| 'fileUploadError'
	| 'invalidFileFormat'
	| 'someTranslationIsEmpty'
	| 'createDeveloper';

export interface IInitialFormData {
	IDSelectedDeveloper: TEditGameSelectedItem;
	IDSelectedCategory: TEditGameSelectedItem;
	applicationId: string;
	name: string;
	versionCode: string;
	versionName: string;
	description: string;
	appStoreLink: string;
	playMarketLink: string;
	iconFilePath: string;
	gameFileData: IFileData;
	IDsSelectedLanguages: TLanguageIDs[];
	translations: IGameTranslation[];
}

export interface IEditGameState {
	needRefetchData: boolean;
	initialFormData: IInitialFormData | null;
	gameID: string;
	visibleForm: TEditGameVisibleForm;
	iconFilePath: string | null;
	iconFileId: TFileId;
	sourceUrlLocally: TSourceUrlLocally;
	fetchedDevelopers: IItemForGetShortInfo[];
	IDSelectedDeveloper: TEditGameSelectedItem;
	needRefetchDevelopers: boolean;
	fetchedCategories: IItemForGetShortInfo[];
	IDSelectedCategory: TEditGameSelectedItem;
	applicationId: string;
	name: string;
	versionCode: string;
	versionName: string;
	description: string;
	appStoreLink: string;
	playMarketLink: string;
	gameFilePath: string | null;
	gameFileData: IFileData;
	fetchedLanguages: IItemForGetShortInfo[];
	IDsSelectedLanguages: TLanguageIDs[];
	dataForSubmit: IEditGame | null;
	typeOfModalWindow: TTypeOfEditGameModalWindow;
	errorResponseData: IServerError<IGameApplicationIdValidationError> | null;
	selectedTranslationTab: TLanguageIDs | null;
	translations: IGameTranslation[];
}

export const editGameInitialState: IEditGameState = {
	needRefetchData: false,
	initialFormData: null,
	gameID: '',
	visibleForm: 'mainForm',
	iconFilePath: null,
	iconFileId: null,
	sourceUrlLocally: null,
	fetchedDevelopers: [],
	IDSelectedDeveloper: null,
	needRefetchDevelopers: false,
	fetchedCategories: [],
	IDSelectedCategory: null,
	applicationId: '',
	name: '',
	versionCode: '',
	versionName: '',
	description: '',
	appStoreLink: '',
	playMarketLink: '',
	gameFilePath: null,
	gameFileData: {
		fileId: '',
		fileHash: null,
		fileName: null,
	},
	fetchedLanguages: [],
	IDsSelectedLanguages: [],
	dataForSubmit: null,
	typeOfModalWindow: null,
	errorResponseData: null,
	selectedTranslationTab: null,
	translations: [],
};

export interface IEditGameContext extends IEditGameState {
	refetchData(value: boolean): void;
	changeVisibleForm(value: TEditGameVisibleForm): void;
	setFile(fileId: TFileId, sourceUrlLocally: TSourceUrlLocally): void;
	deletePhoto(): void;
	setFetchedDevelopers(value: IItemForGetShortInfo[]): void;
	setIDDeveloper(value: TEditGameSelectedItem): void;
	refetchDevelopers(needRefetch: boolean): void;
	setFetchedCategories(value: IItemForGetShortInfo[]): void;
	setIDCategory(value: TEditGameSelectedItem): void;
	changeApplicationId(value: string): void;
	changeName(value: string): void;
	changeVersionCode(value: string): void;
	changeVersionName(value: string): void;
	changeDescription(value: string): void;
	changeAppStoreLink(value: string): void;
	changePlayMarketLink(value: string): void;
	changeFileData(value: IFileData): void;
	setFetchedLanguages(value: IItemForGetShortInfo[]): void;
	setIDsLanguages(value: TLanguageIDs[]): void;
	setDataForSubmit(data: IEditGame | null): void;
	changeTypeOfModalWindow(value: TTypeOfEditGameModalWindow): void;
	showErrorModalWithInfo(value: IServerError<IGameApplicationIdValidationError> | null): void;
	setTranslationTab(value: TLanguageIDs | null): void;
	changeNameTranslation(languageId: TLanguageIDs, value: string): void;
	changeDescriptionTranslation(languageId: TLanguageIDs, value: string): void;
	resetFields(): void;
}

export const EditGameContext = createContext<IEditGameContext>({
	refetchData: () => undefined,
	changeVisibleForm: () => undefined,
	setFile: () => undefined,
	deletePhoto: () => undefined,
	setFetchedDevelopers: () => undefined,
	setIDDeveloper: () => undefined,
	refetchDevelopers: () => undefined,
	setFetchedCategories: () => undefined,
	setIDCategory: () => undefined,
	changeApplicationId: () => undefined,
	changeName: () => undefined,
	changeVersionCode: () => undefined,
	changeVersionName: () => undefined,
	changeDescription: () => undefined,
	changeAppStoreLink: () => undefined,
	changePlayMarketLink: () => undefined,
	changeFileData: () => undefined,
	setFetchedLanguages: () => undefined,
	setIDsLanguages: () => undefined,
	setDataForSubmit: () => undefined,
	changeTypeOfModalWindow: () => undefined,
	showErrorModalWithInfo: () => undefined,
	setTranslationTab: () => undefined,
	changeNameTranslation: () => undefined,
	changeDescriptionTranslation: () => undefined,
	resetFields: () => undefined,
	...editGameInitialState,
});
